import {
    put,
    select,
    takeEvery,
    call,
    all
} from 'redux-saga/effects';
import fp from 'lodash/fp';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import urlJoin from 'url-join';
import queryString from 'query-string';

import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';

import * as actions from './actions';
import * as constants from './constants';
import * as appConstants from '../../common/constants';
import * as appSelectors from '../../app/selectors';
import * as selectors from './selectors';
import * as routerSelectors from '../../common/router/selectors';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';
import { getImpactAnalysisIdList } from '../../api/impactIdSummary';
import { createImpactIdSummaryRows } from '../../api/adapters/impactIdSummaryAdapter';
import {
    searchToFilters, filtersToSearch, urlToSort, encodeSortingIntoUrl
} from '../../common/urlUtils';

const filterHeaderId = 'SummaryIdGrid';

const filterHeaderActions = actionFactory(filterHeaderId);

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), 'admin/idsummary');

export function* filterChanged(forceUpdate) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, filterHeaderId));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            const gridFiltering = yield select(selectors.getDefaultSort);
            const filterSearch = filtersToSearch(filters);
            const gridSearch = encodeSortingIntoUrl(gridFiltering);
            const search = `${filterSearch}&${gridSearch}`;
            const sp = queryString.parse(search);
            let force = false;
            if (forceUpdate) {
                sp.force = 'true';
                force = true;
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            const currentSearch = yield select(routerSelectors.getSearch);
            if (force || !currentSearch) {
                yield put(replace(`${PAGE_ROUTE}${newSearch}`));
            } else if (currentSearch !== newSearch) {
                yield put(push(`${PAGE_ROUTE}${newSearch}`));
            }
        }
    }
}

export function* handleLocationChangedWithoutQuerystring() {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        yield call(filterChanged, false);
    }
}

export function* handleLocationChanged(action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        const { filterDefinitions, defaultFilters } = constants;
        const filters = searchToFilters(
            action.payload.location.search,
            filterDefinitions,
            defaultFilters
        );
        yield put(filterHeaderActions.setFilters(filters));
        const gridsort = fp.get('[0].gridSort')(urlToSort(action.payload.location.search));
        yield put(actions.setAllGridSorts(gridsort));
        const params = queryString.parse(action.payload.location.search);
        const force = params.force === 'true';

        const requestedServerFilters = fp.flow(
            fp.filter(filter => filter.value && filter.definition.server),
            fp.sortBy(filter => filter.definition.id),
            JSON.stringify
        )(filters);
        const currentServerFilters = yield select(selectors.getCurrentServerFilters);
        if (force || (currentServerFilters !== requestedServerFilters)) {
            const dateFrom = fp.flow(
                fp.get('[0].value.from'),
                appConstants.formatDate
            )(filters);
            const dateTo = fp.flow(
                fp.get('[0].value.to'),
                appConstants.formatDate
            )(filters);
            const user = fp.get('[1].value')(filters);
            if (user.length) {
                try {
                    yield put(actions.setLoading());
                    const response = yield call(getImpactAnalysisIdList, dateFrom, dateTo, user);
                    const rows = createImpactIdSummaryRows(response);
                    if (!rows.length) {
                        yield put(actions.getImpactAnalysisIdListEmpty('Fetch Impact Analysis ID list returned an empty list'));
                    }
                    yield put(actions.setRows(rows, requestedServerFilters));
                } catch (error) {
                    yield put(actions.getImpactAnalysisIdListError(error.message));
                }
            } else {
                yield put(actions.setLoading());
                yield put(actions.setRows([], requestedServerFilters));
            }
        }
    }
}

export function* initialise() {
    const initialised = yield (select(selectors.getInitialised));
    if (!initialised) {
        const { defaultFilters, filterDefinitions } = constants;
        yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
        const availableEOD = yield select(appSelectors.getAvailableEOD);
        const filters = fp.set('[0].value', availableEOD)(defaultFilters);
        yield put(filterHeaderActions.setDefaultFilters(filters));
        const search = yield select(routerSelectors.getSearch);
        if (!search) {
            yield put(filterHeaderActions.resetFilters());
        } else {
            const filters = searchToFilters(search, filterDefinitions, defaultFilters);
            const gridsort = fp.get('[0].gridSort')(urlToSort(search));
            yield put(actions.setAllGridSorts(gridsort));
            yield put(filterHeaderActions.setFilters(filters));
        }
        yield call(filterChanged, true);
        yield put(actions.setInitialised());
    }
}

export function* searchId(action) {
    const IMPACT_SUMMARY_PAGE_ROUTE = urlJoin(getVirtualDirectory(), `impactanalysis/${action.id}`);
    yield put(push(`${IMPACT_SUMMARY_PAGE_ROUTE}`));
}

export function* handleUpdate() {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        yield call(filterChanged, false);
    }
}

export function* refreshData() {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        try {
            const filters = yield select(filterHeaderSelectors.getFilters, 'SummaryIdGrid');
            const dateIndex = fp.findIndex(filter => filter.definition.id === 'runDate')(filters);
            const nameIndex = fp.findIndex(filter => filter.definition.id === 'userName')(filters);
            const dateFrom = fp.flow(
                fp.get(`[${dateIndex}].value.from`),
                appConstants.formatDate
            )(filters);
            const dateTo = fp.flow(
                fp.get(`[${dateIndex}].value.to`),
                appConstants.formatDate
            )(filters);
            const user = fp.get(`[${nameIndex}].value`)(filters);
            yield put(actions.setLoading());
            const response = yield call(getImpactAnalysisIdList, dateFrom, dateTo, user);
            const rows = createImpactIdSummaryRows(response);
            if (!rows.length) {
                yield put(actions.getImpactAnalysisIdListEmpty('There are no IDs for this request'));
            }
            yield put(actions.setRows(rows));
        } catch (error) {
            yield put(actions.getImpactAnalysisIdListError(error.message));
        }
    }
}

const testLocation = emptySearch => (action) => {
    if (action.type !== LOCATION_CHANGE) {
        return false;
    }
    if (action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase()) {
        return !!action.payload.location.search !== emptySearch;
    }
    return false;
};

export default function* rootSaga() {
    yield all([
        takeEvery(actions.INITIALISE, initialise),
        takeEvery(actions.SEARCH_ID, searchId),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], handleUpdate),
        takeEvery(actions.SET_GRID_SORTS, filterChanged, false),
        takeEvery(
            testLocation(false),
            handleLocationChanged
        ),
        takeEvery(
            testLocation(true),
            handleLocationChangedWithoutQuerystring
        ),
        takeEvery(actions.REFRESH_DATA, refreshData)
    ]);
}
